import { Box, Divider, Drawer, IconButton, Stack, TextField, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ILocalDisplay } from "../../models/requests/local/localDisplay";
import SelectField, { SelectOption } from "../input/v2/SelectField";
import PrimaryChip from "../input/v2/PrimaryChip";

const localSearchFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 44,
    fontSize: 14,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "rgba(0,0,0,0.12)" },
  },
};

interface LocalPickerDrawerProps {
  open: boolean;
  onClose: () => void;
  availableLocals: ILocalDisplay[];
  selectedLocalIdSet: Set<number>;
  onSelect: (local: ILocalDisplay) => void;
}

const LocalPickerDrawer = ({
  open,
  onClose,
  availableLocals,
  selectedLocalIdSet,
  onSelect,
}: LocalPickerDrawerProps) => {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState("");
  const [buildingFilter, setBuildingFilter] = useState<string>("");
  const [floorFilter, setFloorFilter] = useState<string>("");

  const buildingOptions = useMemo(() => {
    const set = new Set<string>();
    availableLocals.forEach((local) => {
      if (!selectedLocalIdSet.has(local.id)) set.add(local.buildingName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [availableLocals, selectedLocalIdSet]);

  const floorOptions = useMemo(() => {
    const set = new Set<string>();
    availableLocals.forEach((local) => {
      if (selectedLocalIdSet.has(local.id)) return;
      if (buildingFilter && local.buildingName !== buildingFilter) return;
      set.add(local.floorName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [availableLocals, selectedLocalIdSet, buildingFilter]);

  const buildingSelectOptions: SelectOption<string>[] = useMemo(
    () => [{ value: "", label: t("header.all") }, ...buildingOptions.map((b) => ({ value: b, label: b }))],
    [buildingOptions, t]
  );

  const floorSelectOptions: SelectOption<string>[] = useMemo(
    () => [{ value: "", label: t("header.all") }, ...floorOptions.map((f) => ({ value: f, label: f }))],
    [floorOptions, t]
  );

  const filteredLocals = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return availableLocals
      .filter((local) => !selectedLocalIdSet.has(local.id))
      .filter((local) => (buildingFilter ? local.buildingName === buildingFilter : true))
      .filter((local) => (floorFilter ? local.floorName === floorFilter : true))
      .filter((local) => {
        if (!query) return true;
        return (
          local.name.toLowerCase().includes(query) ||
          local.floorName.toLowerCase().includes(query) ||
          local.buildingName.toLowerCase().includes(query)
        );
      })
      .sort((a, b) => {
        const buildingCompare = a.buildingName.localeCompare(b.buildingName);
        if (buildingCompare !== 0) return buildingCompare;
        const floorCompare = a.floorName.localeCompare(b.floorName);
        if (floorCompare !== 0) return floorCompare;
        return a.name.localeCompare(b.name);
      });
  }, [availableLocals, selectedLocalIdSet, buildingFilter, floorFilter, searchText]);

  const activeFiltersCount = Number(Boolean(buildingFilter)) + Number(Boolean(floorFilter)) + Number(Boolean(searchText.trim()));

  const clearFilters = () => {
    setSearchText("");
    setBuildingFilter("");
    setFloorFilter("");
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 500,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
              {t("table.route.addLocal")}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              {t("table.route.pickerHint")}
            </Typography>
          </Box>

          <IconButton onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Divider />

        <Box sx={{ px: 2.5, py: 2 }}>
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              placeholder={t("table.route.searchLocal")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: "rgba(0,0,0,0.26)", fontSize: 20 }} />,
              }}
              sx={localSearchFieldSx}
            />

            <Stack direction="row" spacing={1}>
              <SelectField
                fullWidth
                value={buildingFilter}
                onChange={(value) => {
                  setBuildingFilter(String(value));
                  setFloorFilter("");
                }}
                options={buildingSelectOptions}
                placeholder={t("table.route.filterBuilding")}
              />

              <SelectField
                fullWidth
                value={floorFilter}
                onChange={(value) => setFloorFilter(String(value))}
                options={floorSelectOptions}
                placeholder={t("table.route.filterFloor")}
                disabled={!buildingFilter && floorOptions.length === 0}
              />

            </Stack>

            {activeFiltersCount > 0 && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                {buildingFilter && <PrimaryChip label={buildingFilter} onDelete={() => setBuildingFilter("")} />}
                {floorFilter && <PrimaryChip label={floorFilter} onDelete={() => setFloorFilter("")} />}
                {searchText.trim() && <PrimaryChip label={searchText.trim()} onDelete={() => setSearchText("")} />}
                <Box sx={{ flex: 1 }} />
                <Typography
                  onClick={clearFilters}
                  sx={{ fontSize: 12, fontWeight: 700, cursor: "pointer", color: "primary.main" }}
                >
                  {t("button.clear")}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {filteredLocals.length === 0 && (
            <Typography sx={{ p: 3, textAlign: "center", color: "text.secondary", fontSize: 13 }}>
              {t("table.route.noLocalsAvailable")}
            </Typography>
          )}

          {filteredLocals.map((local) => (
            <Box
              key={local.id}
              onClick={() => onSelect(local)}
              sx={{
                px: 2.5,
                py: 1.5,
                cursor: "pointer",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                "&:hover": { backgroundColor: "rgba(0,0,0,0.03)" },
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{local.name}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocationOnOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  {local.floorName}, {local.buildingName}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

export default LocalPickerDrawer;